import { FC, useEffect, useTransition } from "react";
import { FormProvider } from "react-hook-form";
import { Trans, useTranslation } from "react-i18next";
import { useRecoilValue } from "recoil";

import { PrivacyPDF, RASTER_LANDINGS, TermsPDF } from "@ryde/assets-landings";
import {
  CheckboxControlledMui,
  DropdownTemplate,
  MessagesDisplayContainer,
  PhoneTemplate,
  TextboxTemplate,
} from "@ryde/controls";
import { normalizePhoneNumber } from "@ryde/shared";
import {
  apiDataFetchingLandingAtom,
  LANDING_RYDE_SUPPORT_PHONE,
  landingInitDataAtom,
  landingMessagesAtom,
  useLandingProfile,
  WestNegevChooseStationStepFormFieldsEnum,
} from "@ryde/utils-landings";

import { ContinueButton } from "../../widgets";
import { useStepChooseStationWestNegev } from "./use-step-choose-station.westnegev";

export const StepChooseStationWestNegev: FC = () => {
  const {
    methods,
    handleChooseStation,
    handleConsentToggle,
    isContinueCanBeApply,
    sitesDropdownList,
    unitsDropdownList,
    ageDropdownList,
    preferredStationsDropdownList,
    preferredDestinationStationsList,
  } = useStepChooseStationWestNegev();
  const { watch } = methods;

  const landingMessages = useRecoilValue(landingMessagesAtom);
  const { fetchAndSetProfile } = useLandingProfile();
  const [, startFetchProfileTransition] = useTransition();
  const isApiLoading = useRecoilValue(apiDataFetchingLandingAtom);
  const { t } = useTranslation();
  const { supportCenterPhone } = useRecoilValue(landingInitDataAtom) ?? {};

  const acceptTermsWatchField = watch(
    WestNegevChooseStationStepFormFieldsEnum.AcceptTerms,
  );
  const acceptPrivacyPolicyWatchField = watch(
    WestNegevChooseStationStepFormFieldsEnum.AcceptPrivacyPolicy,
  );

  useEffect(() => {
    startFetchProfileTransition(() => {
      fetchAndSetProfile();
    });
  }, [fetchAndSetProfile]);

  return (
    <FormProvider {...methods}>
      <div className="landing-top-illustration">
        <img src={RASTER_LANDINGS.CarFestImagePNG} alt="Car fest" />
      </div>
      <div className="landing-title landing-title_choose-station">
        {t("landing.chooseStation.titleWestNegev")}
      </div>
      <div className="landing-sub-title">
        {t("landing.chooseStation.subTitleWestNegev")}
      </div>

      <div className="landing-form-field">
        <label htmlFor={WestNegevChooseStationStepFormFieldsEnum.SiteId}>
          {t("landing.formLabel.siteId")}*
        </label>
        <DropdownTemplate
          uniqueAttrId={WestNegevChooseStationStepFormFieldsEnum.SiteId}
          name={WestNegevChooseStationStepFormFieldsEnum.SiteId}
          size="medium"
          formAttributes={{
            selectItems: sitesDropdownList,
            skipDropdownItemsTranslation: true,
          }}
        />
      </div>

      <div className="landing-form-field">
        <label htmlFor={WestNegevChooseStationStepFormFieldsEnum.UnitId}>
          איזור מגורים*
        </label>
        <DropdownTemplate
          uniqueAttrId={WestNegevChooseStationStepFormFieldsEnum.UnitId}
          name={WestNegevChooseStationStepFormFieldsEnum.UnitId}
          size="medium"
          formAttributes={{
            selectItems: unitsDropdownList,
            skipDropdownItemsTranslation: true,
          }}
        />
      </div>

      <div className="landing-form-field">
        <label htmlFor={WestNegevChooseStationStepFormFieldsEnum.Age}>
          {t("landing.formLabel.age")}*
        </label>
        <DropdownTemplate
          uniqueAttrId={WestNegevChooseStationStepFormFieldsEnum.Age}
          name={WestNegevChooseStationStepFormFieldsEnum.Age}
          size="medium"
          formAttributes={{
            selectItems: ageDropdownList,
            skipDropdownItemsTranslation: true,
          }}
        />
      </div>

      <div className="landing-form-field">
        <label htmlFor={WestNegevChooseStationStepFormFieldsEnum.FirstName}>
          {t("landing.formLabel.firstName")}*
        </label>
        <TextboxTemplate
          uniqueAttrId={WestNegevChooseStationStepFormFieldsEnum.FirstName}
          name={WestNegevChooseStationStepFormFieldsEnum.FirstName}
        />
      </div>

      <div className="landing-form-field">
        <label htmlFor={WestNegevChooseStationStepFormFieldsEnum.LastName}>
          {t("landing.formLabel.lastName")}*
        </label>
        <TextboxTemplate
          uniqueAttrId={WestNegevChooseStationStepFormFieldsEnum.LastName}
          name={WestNegevChooseStationStepFormFieldsEnum.LastName}
        />
      </div>

      <div className="landing-form-field">
        <label htmlFor={WestNegevChooseStationStepFormFieldsEnum.PhoneMobile}>
          {t("landing.formLabel.phone")}*
        </label>
        <PhoneTemplate
          uniqueAttrId={WestNegevChooseStationStepFormFieldsEnum.PhoneMobile}
          name={WestNegevChooseStationStepFormFieldsEnum.PhoneMobile}
          sx={{ size: "small" }}
          formAttributes={{ disablePhoneCallable: true, disabled: true }}
        />
      </div>

      <div className="landing-form-field">
        <label
          htmlFor={WestNegevChooseStationStepFormFieldsEnum.PreferredStationId}
        >
          {t("landing.formLabel.preferredStationId")}*
        </label>
        <DropdownTemplate
          uniqueAttrId={
            WestNegevChooseStationStepFormFieldsEnum.PreferredStationId
          }
          name={WestNegevChooseStationStepFormFieldsEnum.PreferredStationId}
          size="medium"
          formAttributes={{
            selectItems: preferredStationsDropdownList,
            skipDropdownItemsTranslation: true,
          }}
        />
      </div>

      <div className="landing-form-field">
        <label htmlFor={WestNegevChooseStationStepFormFieldsEnum.DestinationId}>
          {t("landing.formLabel.destinationStation")}*
        </label>
        <DropdownTemplate
          uniqueAttrId={WestNegevChooseStationStepFormFieldsEnum.DestinationId}
          name={WestNegevChooseStationStepFormFieldsEnum.DestinationId}
          size="medium"
          formAttributes={{
            selectItems: preferredDestinationStationsList,
            skipDropdownItemsTranslation: true,
          }}
        />
      </div>

      <div className="landing-form-field">
        <div className="landing-accept-terms">
          <CheckboxControlledMui
            width={16}
            height={16}
            checked={acceptTermsWatchField ?? false}
            setCheckedToggle={() =>
              handleConsentToggle(
                WestNegevChooseStationStepFormFieldsEnum.AcceptTerms,
              )
            }
          />
          <span className="custom-label">
            {t("landing.formLabel.termsAndConditions")}{" "}
            <a
              href={TermsPDF}
              target="_blank"
              rel="noreferrer"
              aria-label={t("landing.formLabel.termsAndConditions")}
            >
              כאן
            </a>
          </span>
        </div>
      </div>

      <div className="landing-form-field">
        <div className="landing-accept-terms">
          <CheckboxControlledMui
            width={16}
            height={16}
            checked={acceptPrivacyPolicyWatchField ?? false}
            setCheckedToggle={() =>
              handleConsentToggle(
                WestNegevChooseStationStepFormFieldsEnum.AcceptPrivacyPolicy,
              )
            }
          />
          <span className="custom-label">
            {t("landing.formLabel.acceptPrivacyPolicy")}{" "}
            <a
              href={PrivacyPDF}
              target="_blank"
              rel="noreferrer"
              aria-label={t("landing.formLabel.acceptPrivacyPolicy")}
            >
              כאן
            </a>
          </span>
        </div>
      </div>

      <MessagesDisplayContainer
        serverMessages={landingMessages?.messages}
        type={landingMessages?.type}
      />

      <ContinueButton
        disabled={!isContinueCanBeApply}
        onClick={handleChooseStation}
        loading={isApiLoading}
      />

      <div className="landing-container-footer">
        <div className="landing-container-footer-logo">
          <img
            className="landing-logo__img"
            src={RASTER_LANDINGS.RideLogoPNG}
            alt="Ryde logo"
            width="120"
            height="51"
          />
        </div>

        <span className="landing-customer-support custom-label">
          <Trans
            i18nKey="landing.helpDisclaimer"
            values={{
              phoneNumber: normalizePhoneNumber(LANDING_RYDE_SUPPORT_PHONE),
            }}
            components={{
              secure_link: (
                <a
                  aria-label="call center phone number"
                  href={`tel:${LANDING_RYDE_SUPPORT_PHONE}`}
                  target="_blank"
                  rel="noopener noreferrer"
                />
              ),
              br: <br />,
            }}
          />
        </span>
      </div>
    </FormProvider>
  );
};
